import { AnimatePresence, motion } from "framer-motion";
import { Download, Share, Smartphone, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISS_KEY = "orizonte:pwa-install-dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  ("standalone" in window.navigator && Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone));

export const MobilePwaPrompt = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const protectedRoute = [
    "/dashboard",
    "/subscriptions",
    "/future-stack",
    "/calendar",
    "/intelligence",
    "/analytics",
    "/scenarios",
    "/accounts",
    "/teams",
    "/vision",
    "/settings",
  ].includes(location.pathname) || location.pathname === "/admin/readiness";

  const isiOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  }, []);

  useEffect(() => {
    if (!isMobile || isStandalone()) return;
    if (window.localStorage.getItem(DISMISS_KEY) === "true") return;

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || isStandalone()) return;
    if (window.localStorage.getItem(DISMISS_KEY) === "true") return;

    const timeout = window.setTimeout(() => setIsVisible(true), 1600);
    return () => window.clearTimeout(timeout);
  }, [isMobile]);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, "true");
    setIsVisible(false);
  };

  const install = async () => {
    if (!installPrompt) {
      setIsVisible(true);
      return;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") dismiss();
    setInstallPrompt(null);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          className={`fixed inset-x-3 z-[120] mx-auto max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#07100a]/95 p-4 shadow-2xl backdrop-blur-xl md:hidden ${protectedRoute ? "bottom-[92px]" : "bottom-3"}`}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orizonte-lime/50 to-transparent" />
          <div className="flex items-start gap-3">
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-xl border border-orizonte-lime/20 bg-orizonte-lime/10 p-2.5 text-orizonte-lime"
            >
              <Smartphone className="h-5 w-5" />
            </motion.div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-orizonte-text">Use Orizonte like an app</h3>
                  <p className="mt-1 text-xs leading-relaxed text-orizonte-textMuted">
                    Add it to your home screen for a cleaner mobile workspace.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={dismiss}
                  className="rounded-lg p-1.5 text-orizonte-textMuted transition-colors hover:bg-white/5 hover:text-white"
                  aria-label="Dismiss app install prompt"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {isiOS && !installPrompt ? (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-orizonte-textMuted">
                  <Share className="h-4 w-4 text-orizonte-lime" />
                  <span>Tap Share, then Add to Home Screen.</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={install}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orizonte-lime px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-orizonte-lime/90"
                >
                  <Download className="h-4 w-4" />
                  Add to Home Screen
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
